var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
const checkoutElementInstances = {};
/**
 * This creates a checkout element. A checkout element is a piece of UI that is rendered by the checkout SDK
 * with the purpose of hiding complexity from Aphrodite. Such as initializing an interactive piece of UI for
 * a payment method or a complete checkout page.
 *
 * When the SDK wants to render a checkout element the checkout state is updated to include the element in
 * the checkoutElements array. And then this function is used to get a hold of the corresponding
 * DOM instance of that element.
 *
 * Note that there might be a significant delay between the time a checkout element is added to state and that the
 * element is actually rendered into the DOM. This is because the SDK doesn't know when the element should be
 * rendered into the DOM and it's up to the consumer of the SDK to decide that. We might be prepared to render
 * payment methods in the SDK but the customer is not yet on the checkout page. And she might never end up there
 * which means that the onMount callback will never be called.
 *
 * This function helps to hide that complexity since you pass in an onMount callback that will be called when the
 * element is rendered into the DOM. And if the element is removed from the SDK state and the .destroy() method is
 * called on the instance the onMount simply won't be called.
 */
export const elementInstance = ({ elementId, idempotencyKey, onMount, }) => {
    initializeMutationObserverIfNeeded();
    const currentInstance = checkoutElementInstances[elementId];
    const useIdempotencyKey = currentInstance && idempotencyKey !== undefined;
    if (useIdempotencyKey && currentInstance.idempotencyKey !== idempotencyKey) {
        currentInstance.destroy();
    }
    if (useIdempotencyKey && currentInstance.idempotencyKey === idempotencyKey) {
        return currentInstance;
    }
    const instance = createInstance(elementId, idempotencyKey, onMount);
    checkoutElementInstances[elementId] = instance;
    const element = document.getElementById(elementId);
    if (element) {
        callOnMount(instance, element);
    }
    return instance;
};
const createInstance = (elementId, idempotencyKey, onMount) => {
    var _a;
    const instance = {
        elementId,
        idempotencyKey,
        element: (_a = document.getElementById(elementId)) !== null && _a !== void 0 ? _a : undefined,
        onMount,
        onUnmount: undefined,
        metadata: {},
        destroy: () => __awaiter(void 0, void 0, void 0, function* () {
            if (checkoutElementInstances[elementId] === instance) {
                delete checkoutElementInstances[elementId];
            }
            // If instance.element isn't set it means that it's already been unmounted
            // or it never got mounted in the first place and in that case we don't want
            // to call onUnmount
            if (instance.element) {
                callOnUnmount(instance, instance.element);
            }
        }),
    };
    return instance;
};
let observer;
let timer;
const initializeMutationObserverIfNeeded = () => {
    if (observer) {
        return;
    }
    observer = new MutationObserver(() => {
        // This means that we'll only check for mutations once per frame (16ms). The reason for this is that
        // there might be animations or other things that trigger a lot of mutations in a short amount of time
        // and we don't need to react to all of them.
        // Also note that we don't use cancelAnimationFrame here since we want to make sure that we always
        // check for mutations at least once per frame. If we used cancelAnimationFrame we might end up in a
        // situation where we never check for mutations if there's long running animations that trigger lots
        // of mutations.
        if (timer === undefined) {
            timer = requestAnimationFrame(() => {
                timer = undefined;
                onMutation();
            });
        }
    });
    observer.observe(document.body, {
        childList: true,
        subtree: true,
    });
};
const onMutation = () => __awaiter(void 0, void 0, void 0, function* () {
    for (const [elementId, instance] of Object.entries(checkoutElementInstances)) {
        const element = document.getElementById(elementId);
        if (element && !instance.element) {
            yield callOnMount(instance, element);
        }
        else if (!element && instance.element) {
            const previousElement = instance.element;
            yield callOnUnmount(instance, previousElement);
        }
    }
});
const callOnMount = (instance, element) => __awaiter(void 0, void 0, void 0, function* () {
    instance.element = element;
    const maybeOnUnmount = yield instance.onMount(element);
    if (maybeOnUnmount) {
        instance.onUnmount = maybeOnUnmount;
    }
});
const callOnUnmount = (instance, previousElement) => __awaiter(void 0, void 0, void 0, function* () {
    instance.element = undefined;
    const onUnmount = instance.onUnmount;
    if (onUnmount) {
        const callback = yield onUnmount;
        if (callback) {
            callback(previousElement);
            // Since onUnmount is returned from onMount we clear it to avoid calling it twice.
            // If we want to unmount again a new onUnmount will be produced by first calling onMount.
            instance.onUnmount = undefined;
        }
    }
});
//# sourceMappingURL=instance.js.map